import { useEffect, useMemo } from 'react'
import styled from '@emotion/styled'
import { CartItem } from '@open-tender/types'
import { makeUpsellItemsForCart } from '@open-tender/utils'
import {
  selectCart,
  selectCartIds,
  selectContentSection,
  selectMenu,
  toggleSidebar,
  useAppDispatch,
  useAppSelector,
} from '@open-tender/cloud'
import { Body, Heading, SeeMoreLink, UpsellItems } from 'components'
import { sendUpsillsMenuView } from 'app/analytics'
import { makeUpsellsAnalyticsItems } from 'utils/helpers'

const SidebarUpsellView = styled.div`
  flex-grow: 0;
  width: 100%;
  padding: 2rem 0 0;
`

const SidebarUpsellHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 0 2rem 0.5rem;
`

const SidebarUpsellTitle = styled(Heading)`
  display: block;
  font-size: ${(props) => props.theme.fonts.sizes.big};
  @media (max-width: ${(props) => props.theme.breakpoints.mobile}) {
    font-size: ${(props) => props.theme.fonts.sizes.big};
  }
`

const SidebarUpsellSubtitle = styled(Body)`
  display: block;
  margin: 0.75rem 0 0;
  font-size: ${(props) => props.theme.fonts.sizes.small};
  @media (max-width: ${(props) => props.theme.breakpoints.mobile}) {
    margin: 0.5rem 0 0;
    font-size: ${(props) => props.theme.fonts.sizes.xSmall};
  }
`

const SidebarUpsell = () => {
  const dispatch = useAppDispatch()
  const cartIds = useAppSelector(selectCartIds)
  const { categories, soldOut } = useAppSelector(selectMenu)
  const upsells = useAppSelector(selectContentSection('upsells'))
  const { show, title, subtitle } = upsells?.cart || {}
  const cart = useAppSelector(selectCart)
  const upsellItems = useMemo(
    () => makeUpsellItemsForCart(categories, cartIds, soldOut) as CartItem[],
    [categories, cartIds, soldOut]
  )
  const items = useMemo(() => {
    return cart ? makeUpsellsAnalyticsItems(cart, upsellItems, 'CART') : []
  }, [cart, upsellItems])

  useEffect(() => {
    if (show && upsellItems.length) {
      sendUpsillsMenuView({ position: 'CART', items })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show])

  if (!show || !upsellItems.length) return null

  return (
    <SidebarUpsellView>
      <SidebarUpsellHeader>
        <div>
          <SidebarUpsellTitle>{title}</SidebarUpsellTitle>
          {subtitle && (
            <SidebarUpsellSubtitle>{subtitle}</SidebarUpsellSubtitle>
          )}
        </div>
        {upsellItems.length > 2 && (
          <div onClick={() => dispatch(toggleSidebar())}>
            <SeeMoreLink size="small" text="See more" to="/add-ons" />
          </div>
        )}
      </SidebarUpsellHeader>
      <UpsellItems upsellItems={items as CartItem[]} isSmall={true} />
    </SidebarUpsellView>
  )
}

export default SidebarUpsell
